import { useState } from "react";

import { useAlertContext, useConfirmationDialogContext } from "@shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormikProps } from "formik";

import { CaseNoteTemplateResponse, caseNoteTemplatesApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { ApiErrorCode, getApiErrorCode } from "@/common/helper/apiErrorHelper";
import { nameof } from "@/common/helper/commonHelper";
import { CaseNoteFormValues } from "@/pages/clients/client/components/tabs/notes-tab/components/case-note-form/CaseNoteForm.types";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useCaseNoteTemplateSelect(formik: FormikProps<CaseNoteFormValues>, clientId: string) {
  const { t } = useTranslation();
  const { showYesNoDialog } = useConfirmationDialogContext();
  const { showError } = useAlertContext();

  const [selectedTemplate, setSelectedTemplate] = useState<CaseNoteTemplateResponse | null>(null);

  const { data: templates = [] } = useQuery({
    queryKey: [QueryKey.CaseNoteTemplates],
    queryFn: () => caseNoteTemplatesApi.getAllCaseNoteTemplates(),
  });

  const { mutate: resolveTemplate } = useMutation({
    mutationFn: (template: CaseNoteTemplateResponse) =>
      caseNoteTemplatesApi.resolveCaseNoteTemplate(
        {
          id: template.id,
          resolveCaseNoteTemplateRequest: { clientId },
        },
        // The no-active-upload-link 404 is an expected flow; onError toasts for every failure.
        withLocalErrorHandling
      ),
  });

  const handleSelect = (template: CaseNoteTemplateResponse | null) => {
    if (template === null) {
      setSelectedTemplate(null);
      return;
    }

    const applyTemplate = () => {
      setSelectedTemplate(template);
      // The callbacks are passed to mutate (not to useMutation) deliberately: they only fire
      // for the latest mutate call, so a slow response for a previously selected template
      // never overwrites the field.
      resolveTemplate(template, {
        onSuccess: (response) => {
          void formik.setFieldValue(nameof<CaseNoteFormValues>("content"), response.content);
        },
        onError: (error) => {
          // Degrade to the raw template text so the selection still has an effect; unresolved
          // placeholders stay visible and editable (harmless in a case note — nothing is sent out).
          void getApiErrorCode(error).then((code) =>
            showError(
              code === ApiErrorCode.UploadTokenNotFound
                ? t("clients.client.templates.uploadLinkRequired")
                : t("clients.client.caseNotes.form.templateResolveError")
            )
          );
          void formik.setFieldValue(nameof<CaseNoteFormValues>("content"), template.content);
        },
      });
    };

    const currentContent = formik.values.content ?? "";
    if (currentContent.trim().length === 0) {
      applyTemplate();
      return;
    }

    showYesNoDialog(
      t("clients.client.caseNotes.form.templateOverwriteTitle"),
      t("clients.client.caseNotes.form.templateOverwriteText"),
      (confirmed) => {
        if (confirmed) {
          applyTemplate();
        }
      }
    );
  };

  return { templates, selectedTemplate, handleSelect };
}
